import React, { useCallback, useEffect, useRef, useState } from "react";
import { GenericInput } from "../../components/inputs/GenericInput";
import { Box, Button, Card, Divider, Grid, IconButton, MenuItem, Modal, Stack,Typography } from "@mui/material";
import { Checkbox, Collapse, Select, Tabs, TextInput, Textarea } from "@mantine/core";
import { Pencil, Plus, Trash } from "tabler-icons-react";
import { isUndefinedorEmpty } from "../transformer/UnregisteredFunctions";
import { funcNamePattern } from "../transformer/Transformer";
import { getRegisteredFunctions, saveRegisteredFunction } from "../../services/integrationService";
import { useDisclosure } from '@mantine/hooks';
import { functionMap, parameterInfo } from "../../types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { openPopup } from "../../stateManagement/actions/popupActions";

const returnOptions = ["Array", "String", "Number", "Boolean", "Object"];

const initialParamState: parameterInfo = {
  type: "",
  required: false,
  parameterName: "",
  isVariableArgument: false,
  parameterDescription: "",
};

const initialFunctionState: functionMap = {
  id: "",
  language: "javascript",
  description: "",
  returnType: "",
  implementation: "",
  parametersInfo: [],
};

export default function RegisteredFunctionsTabs(){

  const [functionInfoArr, setFunctionInfoArr] = useState<functionMap[]>([]);
  const [selectedFunction,setSelectedFunction] = useState<string>("--none--");

  const selectedFunctionInfo = functionInfoArr.find(s=>s.id === selectedFunction) || initialFunctionState

  const fetchRegisteredFunctions = useCallback(async () => {
      const result = await getRegisteredFunctions();
      if(result.status === 200|| result.status === 201) {
        setFunctionInfoArr(result.data.features.filter((f: functionMap) => f.language === "javascript"));
      } else setFunctionInfoArr([]);
    },[])
  
  useEffect(() => {
    fetchRegisteredFunctions();
  }, [fetchRegisteredFunctions]);
  
  return (
    <Box className="integration-inputs" margin={12}>
      <Tabs tabPadding="lg">
        
        <Tabs.Tab placeholder="" key={"new"} label="Create New Function" icon={<Plus size={14} />} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} >
          <RegisteredFunctions funcMap={functionInfoArr.map(s=>s.id)} iniState={initialFunctionState} isNew={true}
            onSave={() => fetchRegisteredFunctions()} 
          />
        </Tabs.Tab>

        <Tabs.Tab placeholder="" key={"edit"} label="Edit Function" icon={<Pencil size={14} />} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} >
          <GenericInput
            textFieldInputProps={{
              select: true,
              label: "Registered Functions",
              value: selectedFunction,
              children: [
                <MenuItem
                  key={"--none--"}
                  value={"--none--"}
                  children={"--none--"}
                />,
                ...functionInfoArr.map((functioninfo) => ( <MenuItem key={functioninfo.id} value={functioninfo.id} children={functioninfo.id} />)),
              ],
              onChange: (e) => setSelectedFunction(e.target.value),
            }}
          />
          <Divider sx={{ margin: "10px 20px 30px" }} />
          {selectedFunctionInfo !== initialFunctionState && (
            <RegisteredFunctions funcMap={functionInfoArr.map(s=>s.id)}
              key={selectedFunction}
              iniState={selectedFunctionInfo}
              isNew={false}
              onSave={() => fetchRegisteredFunctions()}
            />
          )}
        </Tabs.Tab>
      </Tabs>
    </Box>
  );
}

type ModalState = "create" | "edit" | "none";
export function RegisteredFunctionsButtons(props:{
  afterChange:()=>void
}) {
  const [modalState, setModalState] = useState<ModalState>("none");
  const open = modalState === "create" || modalState === "edit";
  const isEditing = modalState === "edit";

  const [functionInfo, setFunctionInfo] = useState<functionMap[]>([]);
  const [selectedFunctionName, setSelectedFunctionName] = useState<string>("--none--");
  const selectedFunctionInfo =
    functionInfo.find((functionInfoObj) => functionInfoObj.id === selectedFunctionName) || initialFunctionState;

  const fetchRegisteredFunctions = useCallback(async () => {
    const result = await getRegisteredFunctions();
    if (result.status === 200 || result.status === 201) {
      setFunctionInfo(
        result.data.features.filter(
          (f: functionMap) => f.language === "javascript"
        )
      );
    } else setFunctionInfo([]);
  }, []);

  useEffect(() => {
    fetchRegisteredFunctions();
  }, [fetchRegisteredFunctions]);

  const onClose = () => {
    setModalState("none");
    props.afterChange();
  };
  
  return (
    <>
      <Button variant="outlined" color="primary" style={{ marginRight: "10px" }} onClick={() => setModalState("create")}>
        Create Registered Function
      </Button>
      <Button variant="outlined" color="primary" style={{ marginRight: "10px" }} onClick={() => setModalState("edit")}>
        Edit Registered Function
      </Button>
      <Modal open={open} onClose={onClose}>
        <Stack className="registeredfunctions-modal-stack integration-inputs">
        <IconButton style={{position:'absolute',top:"10px", right:"10px"}} onClick={onClose} children={<FontAwesomeIcon icon={faXmark} />} />
          {isEditing ? (
            <>
              <GenericInput
                textFieldInputProps={{
                  select: true,
                  label: "Registered Functions",
                  value: selectedFunctionName,
                  children: [
                    <MenuItem key={"--none--"} value={"--none--"} children={"--none--"}/>,
                    ...functionInfo.map((functionInfoObj) => ( <MenuItem key={functionInfoObj.id} value={functionInfoObj.id} children={functionInfoObj.id} />)),
                  ],
                  onChange: (e) => setSelectedFunctionName(e.target.value),
                }}
              />
              <Divider sx={{ margin: "10px 20px 30px" }} />
              {selectedFunctionInfo !== initialFunctionState && (
                <RegisteredFunctions
                  funcMap={functionInfo.map((functionInfoObj) => functionInfoObj.id)}
                  key={selectedFunctionName}
                  iniState={selectedFunctionInfo}
                  isNew={false}
                  onSave={() => {fetchRegisteredFunctions(); onClose() }}
                />
              )}
            </>
          ) : (
            <RegisteredFunctions
              funcMap={functionInfo.map((functionInfoObj) => functionInfoObj.id)}
              iniState={initialFunctionState}
              isNew={true}
              onSave={() => {fetchRegisteredFunctions(); onClose() }}
            />
          )}
        </Stack>
      </Modal>
    </>
  );
}

function RegisteredFunctions(props: {
  iniState: functionMap;
  funcMap: string[];
  isNew: boolean;
  onSave: () => void;
}) {
  
  const [functionInfo, setFunctionInfo] = useState(props.iniState);
  const ref = useRef<HTMLButtonElement>(null);
  
  async function handleSave() {
    let funcName = functionInfo.id;
    let funcDescription = functionInfo.description;
    let funcBody = functionInfo.implementation
    let funcReturnType = functionInfo.returnType;
    if(!funcName || funcName.trim()=== "" || !funcDescription || funcDescription.trim()==="" || !funcBody || funcBody.trim()==="" || !funcReturnType || funcReturnType === "--none--"){
        openPopup('Alert',"Please fill all fields before saving function")
        return;
    }
    const match = funcBody.match(funcNamePattern);
    if(!match || match.length !==5) {
      openPopup('Alert',"Function body should be of format function function_name(param1,...){}")
      return;
    }
    const leadParams = match[2];
    const trailParams = match[4];
    let count=0;
    if(leadParams) {
        count+=leadParams.split(",").length-1;
    }
    if(trailParams) {
        count+=1;
    }
    if(count !== functionInfo.parametersInfo.length) {
      openPopup('Alert',"count of parameters mismatch with function body")
      return;
    }
    if(funcName !== match[1]) {
      openPopup('Alert',"Function name mismatch with definition in function body")
      return;
    }
    if(props.isNew && props.funcMap.includes(funcName)){
      openPopup('Alert',"Function Name Already Exists! Use another name" )
      return;
    }
    if(validateParamaters() === false){
      openPopup('Alert',"Please Enter valid parameters")
      return;
    }
    let httpVerb, httpPUTEndpoint;
    if(props.isNew){
      httpVerb = "POST";
      httpPUTEndpoint = "";
    } else{
      httpVerb = "PUT";
      httpPUTEndpoint = funcName;
    }
    let body = {
      ...functionInfo,
      enabled:true,
    }
    if(ref.current) 
      ref.current.disabled = true;
    const response = await saveRegisteredFunction(body,httpVerb,httpPUTEndpoint);
    if(response.status === 200 || response.status === 201){
      openPopup('Success',"Function Created Successfully");
    } else {
      openPopup('Error',"Error Creating Function");
    }
    if(ref.current) 
      ref.current.disabled = false;
    props.onSave();
  }

  function validateParamaters(): boolean {
    let paramNameConflict = new Map();
    for (let i = 0; i < functionInfo.parametersInfo.length; i++) {
      let name = functionInfo.parametersInfo[i].parameterName;
      let description = functionInfo.parametersInfo[i].parameterDescription;
      let type = functionInfo.parametersInfo[i].type;
      if ( isUndefinedorEmpty(name) || isUndefinedorEmpty(description) || isUndefinedorEmpty(type)) {
        return false;
      } else {
        if (paramNameConflict.has(name)) {
          alert(`Parameter${i + 1} name conflicts parameter${paramNameConflict.get(name)}!`);
          return false;
        }
        paramNameConflict.set(name, i + 1);
      }
    }
    return true;
  }

  function handleChange(field: string, value: string | boolean, index: number) { // handle parameter component change
    setFunctionInfo({
      ...functionInfo,
      parametersInfo: [
        ...functionInfo.parametersInfo.map((s, idx) =>
          idx === index ? { ...s, [field]: value } : s
        ),
      ],
    });
  }

  function handleDelete(index: number) { // handle parameter component delete
    setFunctionInfo({
      ...functionInfo,
      parametersInfo: [
        ...functionInfo.parametersInfo.filter((_a, idx) => idx !== index),
      ],
    });
  }

  return (
    <Stack spacing={1}>
      <GenericInput
        textFieldInputProps={{
          fullWidth: true,
          label: "Function Language",
          disabled: true,
          value: functionInfo.language,
          onChange: (e) => setFunctionInfo({ ...functionInfo, language: e.target.value }),
        }}
      />
      <GenericInput
        textFieldInputProps={{
          fullWidth: true,
          label: "Function Name",
          value: functionInfo.id,
          disabled: !props.isNew,
          onChange: (e) => setFunctionInfo({ ...functionInfo, id: e.target.value }),
        }}
      />
      <GenericInput
        textFieldInputProps={{
          fullWidth: true,
          multiline: true,
          minRows: 2,
          maxRows: 5,
          label: "Function Description",
          value: functionInfo.description,
          onChange: (e) => setFunctionInfo({ ...functionInfo, description: e.target.value }),
        }}
      />
      <Typography variant="subtitle1">
        Parameter
        <Button
          variant="contained"
          size="small"
          sx={{ margin: "5px 5px", padding: "0", minWidth: "25px" }}
          color="warning"
          children="+"
          onClick={() =>
            setFunctionInfo({
              ...functionInfo,
              parametersInfo: [...functionInfo.parametersInfo, initialParamState],
            })
          }
        />
      </Typography>
      <Grid container alignItems={"center"} sx={{ marginY: "20px !important" }}>
        {functionInfo.parametersInfo.map((paramInfo:parameterInfo, index:number) => (
          <ParameterComp
            key={index}
            {...paramInfo}
            onChange={(field: string, value: string | boolean) =>
              handleChange(field, value, index)
            }
            index={index}
            handleDelete={() => handleDelete(index)}
          />
        ))}
      </Grid>
      <GenericInput
        textFieldInputProps={{
          fullWidth: true,
          multiline: true,
          minRows: 2,
          maxRows: 15,
          label: "Function Body",
          value: functionInfo.implementation,
          onChange: (e) =>
            setFunctionInfo({ ...functionInfo, implementation: e.target.value }),
        }}
      />
      <GenericInput
        textFieldInputProps={{
          fullWidth: true,
          select: true,
          label: "Return Type",
          value: functionInfo.returnType,
          children: returnOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          )),
          onChange: (e) => setFunctionInfo({ ...functionInfo, returnType: e.target.value }),
        }}
      />
      <Button
          variant="outlined"
          size="medium"
          color="primary"
          sx={{maxWidth:"100px"}}
          children={"Save"}
          onClick={handleSave}
          ref={ref}
        />
    </Stack>
  );
}

function ParameterComp(props: {
  index: number;
  type: string;
  required: boolean;
  parameterName: string;
  isVariableArgument: boolean;
  parameterDescription: string;
  onChange: (field: string, value: string | boolean) => void;
  handleDelete: () => void;
}) {
  const [opened, { toggle }] = useDisclosure(false);

  return (
    <Grid item xs={12} sm lg={4} padding={3} minWidth={"300px"}>
      <Card elevation={3} sx={{ padding: "1.5em 2em 1.5em" }}>
        <Button
          onClick={toggle}
          className="registeredfunctions-parameter-collapse-button"
        >{`#${props.index + 1}-${props.parameterName}`}</Button>
        <Collapse in={opened}>
          <TextInput
            label="Parameter Name"
            placeholder="Enter Name"
            required
            size="xs"
            value={props.parameterName}
            onChange={(e) => props.onChange("parameterName", e.target.value)}
          />
          <Select
            zIndex={10000} 
            label="Parameter Type"
            placeholder="Enter Type"
            required
            size="xs"
            value={props.type}
            data={returnOptions}
            onChange={(e) => e !== null && props.onChange("type", e)}
          />
          <Textarea
            label="Parameter Description"
            placeholder="Enter Description"
            required
            size="xs"
            value={props.parameterDescription}
            onChange={(e) =>
              props.onChange("parameterDescription", e.target.value)
            }
          />
          <Stack
            marginY={2}
            spacing={1}
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Checkbox
              label="Required"
              radius="xs"
              size="xs"
              checked={props.required}
              onChange={() => props.onChange("required", !props.required)}
            />
            <Checkbox
              label="isVariableArgument"
              radius="xs"
              size="xs"
              checked={props.isVariableArgument}
              onChange={() =>
                props.onChange("isVariableArgument", !props.isVariableArgument)
              }
            />
          </Stack>
          <Button
            variant="contained"
            size="small"
            color="error"
            sx={{ minWidth: "1px", minHeight: "5px", padding: "2" }}
            children={<Trash width={12} height={15} />}
            onClick={props.handleDelete}
          />
        </Collapse>
      </Card>
    </Grid>
  );
}
